import { Vector3 } from "three";
import { Context } from "../../context";
import {
  DefensivePlayMode,
  DefensivePlayState,
  OffensivePlayState,
  PlayStateManagerCb,
  PositionOfInterest,
} from "../../managers/playstate";
import {
  CameraLandmark,
  CameraLandmarkLookup,
  CameraMode,
  SmoothTimes,
} from "../controller";
import { ControllerStrategy } from "../strategy/strategy";

// Safari 12 does not support PointerEvents API
const isPointerEventsNotSupported = !("PointerEvent" in window);

export class OfftrackRevertControllerStrategy extends ControllerStrategy {
  private offTrackMode = CameraMode.MAP;
  private destroying = false;

  private pointerCnt = 0;

  private pointerDownCb?: (event: PointerEvent) => void;
  private pointerUpCb?: (event: PointerEvent) => void;
  private offtrackMouseCb?: (event: MouseEvent) => void;
  private offtrackKeyCb?: (event: KeyboardEvent) => void;

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) {
      console.error("no controller registered for action strategy");
      return this;
    }

    const { controls, domRef } = this.registered;

    // cleanup callbacks (for off-track behaviour)
    const selfDestruct = (event: MouseEvent | PointerEvent | KeyboardEvent) => {
      if (this.destroying) return;

      this.registered?.setMode(this.offTrackMode, { fromInteraction: true });

      const isPointer = event instanceof PointerEvent;

      const ctor = isPointer ? PointerEvent : MouseEvent;

      const eCopy = new ctor(event.type, event);
      this.destroying = true;
      domRef.dispatchEvent(eCopy);
    };

    this.offtrackMouseCb = (event: MouseEvent) => {
      if (event.button == 2) {
        selfDestruct(event);
      }
    };
    this.offtrackKeyCb = (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowLeft":
        case "ArrowRight":
        case "ArrowUp":
        case "ArrowDown":
          selfDestruct(event);
          break;
      }
    };

    this.pointerDownCb = (event: PointerEvent) => {
      // 3 fingers to get off track on mobile
      const touched3 = event.pointerType == "touch" && ++this.pointerCnt >= 3;

      // TODO: this is very fragile - improve
      // workaround camera controls pointer tracking - this might be broken on phones
      // other than mine - need to test!!!
      //@ts-ignore
      if (touched3) controls._activePointers.length = 2;

      if (touched3 || event.button == 2) {
        selfDestruct(event);
      }
    };

    this.pointerUpCb = (event: PointerEvent) => {
      if (event.pointerType == "touch") {
        this.pointerCnt = Math.max(0, this.pointerCnt - 1);
      }
    };

    domRef?.addEventListener("pointerdown", this.pointerDownCb);
    domRef?.addEventListener("pointerup", this.pointerUpCb);
    isPointerEventsNotSupported &&
      domRef?.addEventListener("mousedown", this.offtrackMouseCb);
    window.addEventListener("keydown", this.offtrackKeyCb);

    return this;
  }

  teardown() {
    super.teardown();

    if (!this.registered) return;

    const dom = this.registered.domRef;
    if (this.pointerDownCb)
      dom?.removeEventListener("pointerdown", this.pointerDownCb);
    if (this.pointerUpCb)
      dom?.removeEventListener("pointerup", this.pointerUpCb);
    if (this.offtrackMouseCb && isPointerEventsNotSupported)
      dom?.removeEventListener("mousedown", this.offtrackMouseCb);
    if (this.offtrackKeyCb && isPointerEventsNotSupported)
      window.removeEventListener("keydown", this.offtrackKeyCb);
  }
}

/**
 * Main defensive auto camera
 */
export class DefensiveActionControllerStrategy extends ControllerStrategy {
  private stateUpdateCb?: PlayStateManagerCb;
  private defaultAutoTrack: boolean = false;
  private defaultDamping: SmoothTimes = SmoothTimes.DEFAULT;

  private initialised = false;

  protected POIs: [
    PositionOfInterest | undefined,
    PositionOfInterest | undefined
  ] = [undefined, undefined];

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) {
      console.error("no controller registered for defensive camera strategy");
      return this;
    }

    if (this.registered.controls) {
      this.defaultDamping = this.registered.controls.smoothTime;
    }

    this.registered.addAuxStrategy(OfftrackRevertControllerStrategy);

    this.defaultAutoTrack = this.registered.state.autoTrack;
    this.registered.state.autoTrack = false;

    return this;
  }

  getHomePosition(): Vector3 {
    return CameraLandmarkLookup[CameraLandmark.HH];
  }

  getZoomToValue(playState: DefensivePlayState): number {
    return 1;
  }

  getPrimaryPointOfInterest(
    playState: DefensivePlayState
  ): PositionOfInterest | undefined {
    return playState.posOfInterest.primary;
  }

  getSecondaryPointOfInterest(
    playState: DefensivePlayState
  ): PositionOfInterest | undefined {
    return playState.posOfInterest.secondary;
  }

  update(deltaS: number, gameTimeDelta: number, frameNum?: number) {
    if (!this.registered) {
      console.error("no controls registered");
      return;
    }

    super.update(deltaS, gameTimeDelta, frameNum);

    // lazy register for play state updates here because if we do it in init, the callback
    // triggering steps on itself (setMode gets called simultaneously in different threads)
    if (!this.initialised) {
      // play state callback
      this.stateUpdateCb = (
        defense: DefensivePlayState,
        _offense: OffensivePlayState
      ) => {
        this.playStateUpdated(defense);
      };

      this.registered.setMode(CameraMode.PAIR, { fromManager: true });

      const playStateManager = this.registered.contextRef.playStateManager;
      playStateManager?.addSubscriber("defensive-cam", this.stateUpdateCb);

      this.initialised = true;
    }

    this.registered.state.tracked = this.POIs[0]?.object;
    this.registered.state.trackedOther = this.POIs[1]?.object;

    if (frameNum != undefined) {
      if (frameNum != this.lastFrameNum) {
        this.resolveDeferals(frameNum);
      }
      this.lastFrameNum = frameNum;
    }
  }

  playStateUpdated(state: DefensivePlayState) {
    const controller = this.registered;
    if (!this.registered || !controller || !controller.controls) return;

    const playState = state;

    let goingHome = true;

    const inPlay = controller.contextRef.gameState.playOngoing();

    // TODO: add back under debug flag - useful for debugging state transitions
    // const primaryStr = playState.posOfInterest.primary?.toString();
    // const secondaryStr = playState.posOfInterest.secondary?.toString();
    // console.error(
    //   `changing action cam game state (${inPlay ? "in" : "out"} play) (${
    //     playState.mode
    //   }): ${primaryStr} & ${secondaryStr}`
    // );

    switch (playState.mode) {
      case DefensivePlayMode.BEGIN:
      case DefensivePlayMode.PITCHING: {
        this.deferedTransitions.clear();

        controller.controls.smoothTime = SmoothTimes.MEDIUM;
        controller.state.homePos = this.getHomePosition().clone();

        controller.controls.zoomTo(this.getZoomToValue(playState), true);

        this.POIs[0] = this.getPrimaryPointOfInterest(playState);
        this.POIs[1] = undefined;
        break;
      }

      case DefensivePlayMode.HIT:
        // TODO: decide between teleporting and softer transition
        // controller.state.teleport = true;
        controller.controls.smoothTime = SmoothTimes.SLOW;
        controller.controls.zoomTo(this.getZoomToValue(playState), true);

        if (inPlay) {
          this.deferedTransitions.add({
            cb: () => {
              this.POIs[0] = this.getPrimaryPointOfInterest(playState);
              this.POIs[1] = this.getSecondaryPointOfInterest(playState);
            },
            remainingFrames: 60,
          });
          goingHome = false;
        }
        break;

      case DefensivePlayMode.THROWING:
      case DefensivePlayMode.FIELDING:
        this.deferedTransitions.clear();

        if (inPlay) {
          goingHome = false;
          controller.controls.smoothTime = SmoothTimes.MEDIUM;
          controller.controls.zoomTo(this.getZoomToValue(playState), true);

          this.POIs[0] = this.getPrimaryPointOfInterest(playState);
          this.POIs[1] = this.getSecondaryPointOfInterest(playState);
        }
        break;

      default:
        console.error("unsupported game state in action cam");
        break;
    }

    if (!goingHome) {
      controller.state.homePos = undefined;
    }
  }

  teardown() {
    super.teardown();

    if (!this.registered) return;

    if (this.registered.controls) {
      this.registered.controls.smoothTime = this.defaultDamping;
      this.registered.controls.zoomTo(1, true);
    }

    this.deferedTransitions.clear();

    this.registered.removeAuxStrategy(OfftrackRevertControllerStrategy);
    this.registered.state.autoTrack = this.defaultAutoTrack;

    const playStateManager = this.registered.contextRef.playStateManager;
    playStateManager?.removeSubscriber("defensive-cam");
  }
}
